import RcInstanceDTO from '@robocorp/robocloud-internal-cds-sdk/lib/sdk/Types';
import { OnDemandWorkerInput } from '~/schemas/workersSchemas';
import { WithWorkspaceId } from '~/types/common';
import {
  NotificationPreferences,
  PaginatedEventLogs,
  RuntimeStateUpdate,
  WorkerUpdate,
} from '~/types/resources';
import { dashboardApi, dashboardWorkspaceApi, integrationRuntimeApi } from '~/utils/api';

export const getAll = async ({ workspaceId }: WithWorkspaceId) =>
  dashboardWorkspaceApi.get<RcInstanceDTO.Runtime[]>(`/workspaces/${workspaceId}/runtimes`);

export const getOneById = async ({ workspaceId, id }: { id: string } & WithWorkspaceId) =>
  dashboardWorkspaceApi.get<RcInstanceDTO.Runtime>(`/workspaces/${workspaceId}/runtimes/${id}`);

export const linkRuntime = async ({ workspaceId }: WithWorkspaceId) =>
  dashboardWorkspaceApi.post<string>(`/workspaces/${workspaceId}/runtimes/rtlink`);

export const editOneById = async ({
  workspaceId,
  runtimeId,
  runtime,
}: { runtimeId: string; runtime: RcInstanceDTO.Runtime } & WithWorkspaceId) =>
  dashboardWorkspaceApi.put<RcInstanceDTO.Runtime>(
    `/workspaces/${workspaceId}/runtimes/${runtimeId}`,
    {
      ...runtime,
    },
  );

export const deleteOneById = async ({ workspaceId, id }: { id: string } & WithWorkspaceId) =>
  dashboardWorkspaceApi.delete<RcInstanceDTO.Runtime>(`/workspaces/${workspaceId}/runtimes/${id}`);

export const createEnvironmentGroup = async ({
  workspaceId,
  name,
}: { name: string } & WithWorkspaceId) =>
  dashboardWorkspaceApi.post<RcInstanceDTO.EnvironmentGroup>(
    `/workspaces/${workspaceId}/environmentGroups`,
    { name },
  );

export const listEnvironmentGroups = async ({ workspaceId }: WithWorkspaceId) =>
  dashboardWorkspaceApi.get<RcInstanceDTO.EnvironmentGroup[]>(
    `/workspaces/${workspaceId}/environmentGroups`,
  );

export const getEnvironmentGroupById = async ({
  workspaceId,
  groupId,
}: { groupId: string } & WithWorkspaceId) =>
  dashboardWorkspaceApi.get<RcInstanceDTO.EnvironmentGroup>(
    `/workspaces/${workspaceId}/environmentGroups/${groupId}`,
  );

export const editEnvironmentGroupById = async ({
  workspaceId,
  groupId,
  group,
}: {
  groupId: string;
  group: RcInstanceDTO.EnvironmentGroup;
} & WithWorkspaceId) =>
  dashboardWorkspaceApi.put<RcInstanceDTO.EnvironmentGroup>(
    `/workspaces/${workspaceId}/environmentGroups/${groupId}`,
    { ...group },
  );

export const deleteEnvironmentGroupById = async ({
  workspaceId,
  id,
}: { id: string } & WithWorkspaceId) =>
  dashboardWorkspaceApi.delete<RcInstanceDTO.EnvironmentGroup>(
    `/workspaces/${workspaceId}/environmentGroups/${id}`,
  );

export const addEnvironmentToGroup = async ({
  workspaceId,
  groupId,
  runtimeId,
}: { groupId: string; runtimeId: string } & WithWorkspaceId) =>
  dashboardWorkspaceApi.post<RcInstanceDTO.EnvironmentGroup>(
    `/workspaces/${workspaceId}/environmentGroups/${groupId}/runtimes/${runtimeId}`,
  );

export const removeEnvironmentFromGroup = async ({
  workspaceId,
  groupId,
  runtimeId,
}: { groupId: string; runtimeId: string } & WithWorkspaceId) =>
  dashboardWorkspaceApi.delete<RcInstanceDTO.EnvironmentGroup>(
    `/workspaces/${workspaceId}/environmentGroups/${groupId}/runtimes/${runtimeId}`,
  );

export const addEnvironmentGroupLinkKey = async ({
  workspaceId,
  params,
}: {
  params: Pick<
    RcInstanceDTO.WorkerLinkKey,
    'environment_group_id' | 'name' | 'expiry_ts' | 'autoremove_minutes'
  >;
} & WithWorkspaceId) =>
  dashboardWorkspaceApi.post<RcInstanceDTO.WorkerLinkKey>(
    `/workspaces/${workspaceId}/environmentGroups/${params.environment_group_id}/rtlink`,
    {
      name: params.name,
      expiry_ts: params.expiry_ts,
      autoremove_minutes: params.autoremove_minutes,
    },
  );

export const deleteEnvironmentGroupLinkKey = async ({
  workspaceId,
  keyId,
}: { keyId: string } & WithWorkspaceId) =>
  dashboardWorkspaceApi.delete<RcInstanceDTO.WorkerLinkKey>(
    `/workspaces/${workspaceId}/rtlink/${keyId}`,
  );

export const getEnvironmentGroupLinkKey = async ({
  workspaceId,
  keyId,
}: { keyId: string } & WithWorkspaceId) =>
  dashboardWorkspaceApi.get<RcInstanceDTO.WorkerLinkKey>(
    `/workspaces/${workspaceId}/rtlink/${keyId}`,
  );

export const updateEnvironmentGroupLinkKey = async ({
  workspaceId,
  params,
}: {
  params: Pick<RcInstanceDTO.WorkerLinkKey, 'name' | 'id' | 'expiry_ts' | 'autoremove_minutes'>;
} & WithWorkspaceId) =>
  dashboardWorkspaceApi.put<RcInstanceDTO.WorkerLinkKey>(
    `/workspaces/${workspaceId}/rtlink/${params.id}`,
    {
      name: params.name,
      expiry_ts: params.expiry_ts,
      autoremove_minutes: params.autoremove_minutes,
    },
  );

export const listEnvironmentGroupLinkKeys = async ({
  workspaceId,
  groupId,
}: { groupId: string } & WithWorkspaceId) =>
  dashboardWorkspaceApi.get<RcInstanceDTO.WorkerLinkKey[]>(
    `/workspaces/${workspaceId}/environmentGroups/${groupId}/rtlinks`,
  );

export const createOnDemandRuntime = async ({
  workspaceId,
  worker,
}: {
  worker: OnDemandWorkerInput;
} & WithWorkspaceId) =>
  dashboardWorkspaceApi.post<RcInstanceDTO.OnDemandRuntime>(
    `/workspaces/${workspaceId}/onDemandRuntimes`,
    worker,
  );

export const listOnDemandRuntimes = async ({ workspaceId }: WithWorkspaceId) =>
  dashboardWorkspaceApi.get<RcInstanceDTO.OnDemandRuntime[]>(
    `/workspaces/${workspaceId}/onDemandRuntimes`,
  );

export const getOnDemandRuntimeById = async ({
  workspaceId,
  onDemandRuntimeId,
}: { onDemandRuntimeId: string } & WithWorkspaceId) =>
  dashboardWorkspaceApi.get<RcInstanceDTO.OnDemandRuntime>(
    `/workspaces/${workspaceId}/onDemandRuntimes/${onDemandRuntimeId}`,
  );
export const editOnDemandRuntimeById = async ({
  workspaceId,
  onDemandRuntimeId,
  onDemandRuntime,
}: {
  onDemandRuntimeId: string;
  onDemandRuntime: RcInstanceDTO.OnDemandRuntime;
} & WithWorkspaceId) =>
  dashboardWorkspaceApi.put<RcInstanceDTO.OnDemandRuntime>(
    `/workspaces/${workspaceId}/onDemandRuntimes/${onDemandRuntimeId}`,
    {
      ...onDemandRuntime,
    },
  );
export const deleteOnDemandRuntimeById = async ({
  workspaceId,
  id,
}: { id: string } & WithWorkspaceId) =>
  dashboardWorkspaceApi.delete<RcInstanceDTO.OnDemandRuntime>(
    `/workspaces/${workspaceId}/onDemandRuntimes/${id}`,
  );

export const getRuntimeMessageLog = ({
  workspaceId,
  runtimeId,
}: {
  workspaceId: string;
  runtimeId: string;
}) =>
  integrationRuntimeApi.get<RuntimeStateUpdate[][]>(
    `/workspaces/${workspaceId}/roboworkers/${runtimeId}/messagelog`,
  );

export const getRuntimeEventLogs = ({
  workspaceId,
  runtimeId,
  startAt,
  endAt,
  cursor,
}: {
  runtimeId: string;
  startAt: string;
  endAt: string;
  cursor?: string;
} & WithWorkspaceId) =>
  dashboardApi.get<PaginatedEventLogs>(
    `/workspaces/${workspaceId}/workers/${runtimeId}/eventlogs`,
    {
      startAt,
      endAt,
      cursor,
    },
  );

export const getRuntimeNotificationPreferences = ({ workspaceId }: WithWorkspaceId) =>
  dashboardApi.get<NotificationPreferences>(`/workspaces/${workspaceId}/notifications/workers`);

export const saveRuntimeNotificationPreferences = (
  { workspaceId }: WithWorkspaceId,
  preferences: NotificationPreferences,
) =>
  dashboardApi.put<NotificationPreferences>(
    `/workspaces/${workspaceId}/notifications/workers`,
    preferences,
  );

export const updateWorker = (
  { workspaceId }: WithWorkspaceId,
  { workerId, ...update }: WorkerUpdate,
) => dashboardApi.put<WorkerUpdate>(`/workspaces/${workspaceId}/workers/${workerId}`, update);
